import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { HardDrive, CheckCircle2, AlertCircle, Mail, Copy, BellRing, Palette, Rocket, Trash2 } from "lucide-react";
import { api, errMsg, fmtDate, timeAgo } from "@/lib/api";
import { PageHeader, Section } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import { AssetTaxonomySettings } from "@/components/catalog/AssetTaxonomySettings";

function CheckRow({ ok, label, hint, action, testId }) {
  return (
    <div className="flex items-start gap-3 py-3 border-b border-slate-100 last:border-0" data-testid={testId}>
      {ok ? <CheckCircle2 size={18} className="text-emerald-600 mt-0.5 shrink-0" /> : <AlertCircle size={18} className="text-amber-500 mt-0.5 shrink-0" />}
      <div className="flex-1 min-w-0"><div className="text-sm font-medium">{label}</div><div className="text-xs text-slate-500">{hint}</div></div>
      {action}
    </div>
  );
}

function BrandingCard() {
  const { brand, loadBrand } = useAuth();
  const [form, setForm] = useState(brand);
  const [busy, setBusy] = useState(false);
  useEffect(() => setForm(brand), [brand]);
  const save = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await api.put("/settings/branding", { agency_name: form.agency_name, logo_light: form.logo_light, logo_dark: form.logo_dark, default_currency: form.default_currency }); await loadBrand(); toast.success("Branding saved"); } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <Section title="Branding" testId="branding-settings">
      <form onSubmit={save} className="space-y-4">
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-1.5"><Label className="text-xs font-semibold text-slate-600">Agency name</Label><Input value={form.agency_name || ""} onChange={(e) => setForm({ ...form, agency_name: e.target.value })} data-testid="brand-name-input" /></div>
          <div className="space-y-1.5"><Label className="text-xs font-semibold text-slate-600">Default currency</Label>
            <Select value={form.default_currency} onValueChange={(v) => setForm({ ...form, default_currency: v })}><SelectTrigger data-testid="brand-currency-select"><SelectValue /></SelectTrigger><SelectContent>{["INR", "USD", "EUR", "GBP"].map((c) => <SelectItem key={c} value={c} data-testid={`option-currency-${c}`}>{c}</SelectItem>)}</SelectContent></Select></div>
          <div className="space-y-1.5"><Label className="text-xs font-semibold text-slate-600">Logo for dark surfaces (URL)</Label><Input value={form.logo_light || ""} onChange={(e) => setForm({ ...form, logo_light: e.target.value })} data-testid="brand-logo-light-input" /><div className="bg-[#0F172A] rounded p-3"><img src={form.logo_light} alt="" className="h-8 w-auto" /></div></div>
          <div className="space-y-1.5"><Label className="text-xs font-semibold text-slate-600">Logo for light surfaces (URL)</Label><Input value={form.logo_dark || ""} onChange={(e) => setForm({ ...form, logo_dark: e.target.value })} data-testid="brand-logo-dark-input" /><div className="bg-white border border-slate-200 rounded p-3"><img src={form.logo_dark} alt="" className="h-8 w-auto" /></div></div>
        </div>
        <p className="text-xs text-slate-500 flex items-center gap-1.5"><Palette size={12} />Paste a hosted image link (PNG/WEBP/SVG) to replace either logo. Used in the app, PDFs and emails.</p>
        <Button type="submit" disabled={busy} className="bg-slate-900 hover:bg-slate-800" data-testid="brand-save-btn">{busy ? "Saving…" : "Save branding"}</Button>
      </form>
    </Section>
  );
}

function AccountCard() {
  const [form, setForm] = useState({ current_password: "", new_password: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await api.post("/auth/change-password", form); toast.success("Password updated"); setForm({ current_password: "", new_password: "" }); } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <Section title="Owner password" testId="account-settings">
      <form onSubmit={submit} className="grid sm:grid-cols-3 gap-3 items-end">
        <div className="space-y-1.5"><Label className="text-xs font-semibold text-slate-600">Current password</Label><Input type="password" required value={form.current_password} onChange={(e) => setForm({ ...form, current_password: e.target.value })} data-testid="current-password-input" /></div>
        <div className="space-y-1.5"><Label className="text-xs font-semibold text-slate-600">New password (8+ chars)</Label><Input type="password" required minLength={8} value={form.new_password} onChange={(e) => setForm({ ...form, new_password: e.target.value })} data-testid="new-password-input" /></div>
        <Button type="submit" disabled={busy} className="bg-slate-900 hover:bg-slate-800" data-testid="change-password-btn">Change password</Button>
      </form>
      <p className="text-xs text-slate-500 mt-3">The demo owner password (Admin@123) is public in this build — change it before sharing the live link.</p>
    </Section>
  );
}

function StorageReportCard() {
  const [report, setReport] = useState(null);
  useEffect(() => { api.get("/storage/in-app-report").then((r) => setReport(r.data)).catch(() => {}); }, []);
  const fmt = (b) => { if (!b) return "0 B"; const u = ["B", "KB", "MB", "GB", "TB"]; const i = Math.floor(Math.log(b) / Math.log(1024)); return `${(b / Math.pow(1024, i)).toFixed(i ? 1 : 0)} ${u[i]}`; };
  return (
    <Section title="Files stored in the app" testId="storage-report">
      {!report ? <p className="text-sm text-slate-500">Checking…</p> : (
        <div className="space-y-1 text-sm" data-testid="storage-report-body">
          <p className="text-slate-500 mb-2">Full files still kept inside the app (not on Drive). This is a report only — nothing is moved or deleted.</p>
          <div className="flex justify-between border-b border-slate-100 py-1.5"><span>Uploaded asset files</span><span className="num font-medium" data-testid="storage-report-assets">{report.asset_files.count} · {fmt(report.asset_files.bytes)}</span></div>
          <div className="flex justify-between border-b border-slate-100 py-1.5"><span>Client logo thumbnails</span><span className="num font-medium" data-testid="storage-report-logos">{report.logos.count} · {fmt(report.logos.bytes)}</span></div>
          <div className="flex justify-between py-1.5 font-semibold"><span>Total in-app storage</span><span className="num" data-testid="storage-report-total">{fmt(report.total_bytes)}</span></div>
        </div>
      )}
    </Section>
  );
}

function GoLiveCard({ drive, onDriveConnect }) {
  const [c, setC] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/settings/checklist").then((r) => setC(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const clear = async () => {
    if (!window.confirm("Clear demo data?\n\nThis deletes ALL clients, projects, tasks, goals, proposals, invoices, expenses, deliverables, time logs and alerts currently in the workspace (demo and any you added), plus the four demo logins (Alex, Sarah, Marcus, Neptune client). Your owner account, team logins and branding are kept. This cannot be undone.")) return;
    setBusy(true);
    try { await api.post("/settings/clear-demo"); toast.success("Demo data cleared — ready for real clients"); load(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  if (!c) return null;
  const done = [c.drive_connected, c.email_configured, !c.demo_present, c.password_changed].filter(Boolean).length;
  return (
    <Section title={`Go-live checklist · ${done}/4`} testId="golive-checklist" action={<Rocket size={16} className="text-blue-600" />}>
      <CheckRow ok={c.password_changed} label="Owner password changed" hint={c.password_changed ? "Default demo password replaced" : "The demo password Admin@123 is public — change it below"} testId="check-password" />
      <CheckRow ok={c.drive_connected} label="Google Drive connected" hint={c.drive_connected ? `Connected as ${c.drive_account}` : c.drive_configured ? "Credentials set — approve once with your Google account" : "Add Google OAuth credentials first"} testId="check-drive"
        action={!c.drive_connected && c.drive_configured && <Button size="sm" variant="outline" onClick={onDriveConnect} data-testid="check-drive-connect-btn">Connect</Button>} />
      <CheckRow ok={c.email_configured} label="Email delivery key" hint={c.email_configured ? "Proposals and task alerts are emailed" : "Optional — without it, proposals are portal-only and alerts are logged"} testId="check-email" />
      <CheckRow ok={!c.demo_present} label="Demo data cleared" hint={c.demo_present ? `${c.counts.clients} clients · ${c.counts.projects} projects currently include sample records` : "Workspace holds real data only"} testId="check-demo"
        action={c.demo_present && <Button size="sm" variant="outline" className="text-rose-600 border-rose-200 hover:bg-rose-50" disabled={busy} onClick={clear} data-testid="clear-demo-btn"><Trash2 size={13} className="mr-1" />Clear demo data</Button>} />
      <div className="mt-4 text-xs text-slate-500">Public link: <span className="num text-slate-700">{c.public_base}</span></div>
    </Section>
  );
}

export default function Settings() {
  const [params, setParams] = useSearchParams();
  const [drive, setDrive] = useState(null);
  const [driveDiagnostic, setDriveDiagnostic] = useState(null);
  const handledDriveOutcome = useRef(null);
  const [busy, setBusy] = useState(false);
  const [alerts, setAlerts] = useState([]);
  const [sending, setSending] = useState(false);
  const loadAlerts = useCallback(() => {
    api.get("/alerts/recent").then((r) => setAlerts(r.data)).catch(() => {});
  }, []);
  const load = useCallback(() => {
    api.get("/drive/status").then((r) => setDrive(r.data)).catch((e) => toast.error(errMsg(e)));
    loadAlerts();
  }, [loadAlerts]);
  const sendNow = async () => { setSending(true); try { const { data } = await api.post("/alerts/overdue-now"); toast.success(`${data.overdue_tasks} overdue tasks · ${data.recipients} recipients · ${data.emails_sent} emails sent`); loadAlerts(); } catch (e) { toast.error(errMsg(e)); } finally { setSending(false); } };
  useEffect(() => {
    const loadDriveOutcome = async () => {
      load();
      const outcome = params.get("drive");
      const attemptId = params.get("attempt");
      const reason = params.get("reason");
      const outcomeKey = [outcome, attemptId, reason].filter(Boolean).join(":");
      if (!outcome || handledDriveOutcome.current === outcomeKey) return;
      handledDriveOutcome.current = outcomeKey;
      if (attemptId) {
        try {
          const { data } = await api.get(`/drive/diagnostics/${attemptId}`);
          setDriveDiagnostic(data);
          if (outcome === "connected") toast.success("Google Drive connected");
          if (outcome === "error") toast.error(data.description || "Google Drive connection failed");
          setParams({}, { replace: true });
          return;
        } catch (e) {
          if (e.response?.status === 404) {
            setDriveDiagnostic({
              status: "expired",
              error: "result_expired",
              description: "This connection result is no longer available. Start a fresh connection attempt.",
            });
            toast.error("This connection result has expired. Start a fresh connection attempt.");
            setParams({}, { replace: true });
            return;
          }
          toast.error(errMsg(e));
        }
      }
      if (outcome === "connected") toast.success("Google Drive connected");
      if (outcome === "error") {
        toast.error("Google Drive connection failed. Start a fresh connection attempt and try again.");
      }
      setParams({}, { replace: true });
    };
    loadDriveOutcome();
  }, [load, params, setParams]);

  const connect = async () => { setBusy(true); try { const { data } = await api.get("/drive/connect"); window.location.href = data.authorization_url; } catch (e) { toast.error(errMsg(e)); setBusy(false); } };
  const disconnect = async () => { if (!window.confirm("Disconnect Google Drive?")) return; await api.post("/drive/disconnect"); toast.success("Disconnected"); load(); };
  const copy = (t) => { navigator.clipboard.writeText(t); toast.success("Copied"); };

  return (
    <div data-testid="settings-page">
      <PageHeader eyebrow="Workspace" title="Settings" subtitle="Branding, go-live checklist and the integrations that power delivery and client communication." />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <GoLiveCard drive={drive} onDriveConnect={connect} />
        <BrandingCard />
        <AccountCard />
        <StorageReportCard />
        <Section title="Google Drive" testId="drive-settings">
          {!drive ? <p className="text-sm text-slate-500">Checking…</p> : (
            <div>
              <div className="flex items-start gap-4">
                <div className="h-12 w-12 rounded-md bg-slate-900 text-white grid place-items-center"><HardDrive size={20} /></div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 font-medium" data-testid="drive-status">
                    {drive.connected ? <><CheckCircle2 size={16} className="text-emerald-600" />Connected{drive.account_email && ` as ${drive.account_email}`}</> : drive.configured ? <><AlertCircle size={16} className="text-amber-500" />Not connected</> : <><AlertCircle size={16} className="text-slate-400" />Credentials not set</>}
                  </div>
                  <p className="text-sm text-slate-500 mt-1">
                    {drive.connected ? `Project folders are created in this Drive and shared with the client's email. Connected ${fmtDate(drive.connected_at)}.` : drive.configured ? "Authorize the agency Google account that will hold all client delivery folders." : "Add GOOGLE_CLIENT_ID and GOOGLE_CLIENT_SECRET (Google Cloud → OAuth client) to enable Drive delivery. Until then, you can still attach share links as deliverables."}
                  </p>
                </div>
              </div>
              <div className="mt-5 flex gap-2">
                {drive.connected ? <Button variant="outline" onClick={disconnect} data-testid="drive-disconnect-btn">Disconnect</Button> : <Button className="bg-blue-600 hover:bg-blue-700" disabled={!drive.configured || busy} onClick={connect} data-testid="drive-connect-btn">Connect Google Drive</Button>}
              </div>
              {driveDiagnostic && (
                <div
                  className={`mt-4 border px-3 py-3 text-sm ${driveDiagnostic.status === "connected" ? "border-emerald-200 bg-emerald-50 text-emerald-800" : "border-amber-200 bg-amber-50 text-amber-900"}`}
                  data-testid="drive-connection-outcome"
                >
                  <div className="font-medium" data-testid="drive-connection-message">
                    {driveDiagnostic.status === "connected"
                      ? "Google Drive is connected."
                      : driveDiagnostic.description || "Google Drive connection was not completed."}
                  </div>
                  {driveDiagnostic.status !== "connected" && (
                    <details className="mt-2 text-xs" data-testid="drive-diagnostic-details">
                      <summary className="cursor-pointer font-semibold">Connection details</summary>
                      <dl className="mt-2 space-y-1 text-slate-600" data-testid="drive-diagnostic-content">
                        <div>
                          <dt className="inline font-medium">Google response: </dt>
                          <dd className="inline">{driveDiagnostic.error || "No response code was returned."}</dd>
                        </div>
                        <div>
                          <dt className="inline font-medium">Callback address: </dt>
                          <dd className="inline break-all">{driveDiagnostic.redirect_uri}</dd>
                        </div>
                        <div data-testid="drive-requested-scopes">
                          <dt className="inline font-medium">Requested permission: </dt>
                          <dd className="inline break-all">
                            {(driveDiagnostic.requested_scopes || []).join(" ") || "Not recorded."}
                          </dd>
                        </div>
                        <div data-testid="drive-granted-scopes">
                          <dt className="inline font-medium">Granted permission: </dt>
                          <dd className="inline break-all">
                            {(driveDiagnostic.granted_scopes || []).join(" ")
                              || "Google did not return a token scope."}
                          </dd>
                        </div>
                      </dl>
                    </details>
                  )}
                </div>
              )}
              <div className="mt-5 pt-4 border-t border-slate-100">
                <div className="overline-label mb-2">Authorized redirect URI (paste in Google Cloud Console)</div>
                <button onClick={() => copy(drive.redirect_uri)} className="flex items-center gap-2 text-xs num text-slate-700 bg-slate-50 border border-slate-200 rounded px-3 py-2 w-full text-left hover:border-slate-400" data-testid="copy-redirect-uri-btn"><span className="truncate flex-1">{drive.redirect_uri}</span><Copy size={13} /></button>
              </div>
            </div>
          )}
        </Section>
        <Section title="Proposal emails" testId="email-settings">
          <div className="flex items-start gap-4">
            <div className="h-12 w-12 rounded-md bg-slate-900 text-white grid place-items-center"><Mail size={20} /></div>
            <div className="flex-1">
              <div className="font-medium">Resend</div>
              <p className="text-sm text-slate-500 mt-1">When a proposal is shared, the client can receive a branded email with the scope, pricing and a link to accept in their portal. Requires RESEND_API_KEY; without it, proposals are still delivered through the client portal and marked "portal only".</p>
            </div>
          </div>
        </Section>
        <div className="lg:col-span-2">
          <AssetTaxonomySettings />
        </div>
        <Section title="Task alerts" className="lg:col-span-2" testId="alerts-settings" action={<Button size="sm" variant="outline" disabled={sending} onClick={sendNow} data-testid="send-overdue-now-btn"><BellRing size={14} className="mr-1" />Send overdue reminders now</Button>}>
          <p className="text-sm text-slate-500">Team members get an email the moment a task is assigned to them, plus a daily digest of their overdue tasks at 9:00 IST. Both need the Resend key; until then alerts are logged here as "not sent".</p>
          <div className="mt-4 divide-y divide-slate-100">
            {alerts.map((a) => <div key={a.id} className="flex items-center gap-3 py-2 text-sm" data-testid={`alert-${a.id}`}><span className={`h-2 w-2 rounded-full ${a.sent ? "bg-emerald-500" : "bg-slate-300"}`} /><span className="font-medium capitalize">{a.type.replace("_", " ")}</span><span className="text-slate-500 truncate flex-1">→ {a.email}{a.task_count ? ` · ${a.task_count} tasks` : ""}</span><span className={`text-xs ${a.sent ? "text-emerald-600" : "text-slate-400"}`}>{a.sent ? "sent" : "not sent"}</span><span className="text-xs text-slate-400">{timeAgo(a.created_at)}</span></div>)}
            {!alerts.length && <p className="text-sm text-slate-400 mt-2">No alerts yet.</p>}
          </div>
        </Section>
      </div>
    </div>
  );
}
